import { Eye } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import EditableUnitChip from './EditableUnitChip';
import {
  PREVIEW_EXPANSION,
  addFlatToBlock,
  applyFlatLabelOverride,
  generateFloorPreviews,
  isMorePreviewLabel,
  nextPreviewExpansion,
  removeFlatFromBlock,
  validateFlatLabelChange,
} from './communitySiteUtils';

const FlatPreviewTable = ({ block, onBlockChanged }) => {
  const [expansion, setExpansion] = useState(PREVIEW_EXPANSION.COLLAPSED);

  useEffect(() => {
    setExpansion(PREVIEW_EXPANSION.COLLAPSED);
  }, [block.floors, block.flatsPerFloor]);

  const previews = generateFloorPreviews(block, { expansion });
  if (previews.length === 0) return null;

  const editable = Boolean(onBlockChanged);

  return (
    <div className="community-flat-preview">
      <div className="community-flat-preview-header">
        <Eye className="w-4 h-4 text-teal-700 shrink-0" />
        <span>Auto Generate Preview</span>
      </div>
      {editable && (
        <p className="community-flat-preview-hint">
          Tap a unit to edit, × to remove, or add a flat/room per floor. Duplicates are not
          allowed.
        </p>
      )}
      <div className="community-flat-preview-body">
        {previews.map(row => {
          if (row.floor === -1) {
            return (
              <button
                key="more-floors"
                type="button"
                className="community-flat-preview-more"
                onClick={() => setExpansion(nextPreviewExpansion(expansion))}
              >
                {row.flats[0]}
              </button>
            );
          }

          return (
            <div key={row.floor} className="community-flat-preview-row">
              <span className="community-flat-preview-floor">Floor {row.floor}</span>
              <div className="community-flat-preview-units">
                {row.flats.map((flatLabel, index) => {
                  const flatIndexOnFloor = row.flatIndices?.[index] ?? index + 1;
                  const isMore = isMorePreviewLabel(flatLabel);
                  if (isMore) {
                    return (
                      <button
                        key={`${row.floor}-more`}
                        type="button"
                        className="community-flat-preview-more inline"
                        onClick={() => setExpansion(nextPreviewExpansion(expansion))}
                      >
                        {flatLabel}
                      </button>
                    );
                  }

                  return (
                    <EditableUnitChip
                      key={`${row.floor}-${flatIndexOnFloor}`}
                      label={flatLabel}
                      editable={editable}
                      validate={value =>
                        validateFlatLabelChange(
                          block,
                          row.floor,
                          flatIndexOnFloor,
                          value
                        )
                      }
                      onSubmitted={value =>
                        onBlockChanged?.(
                          applyFlatLabelOverride(
                            block,
                            row.floor,
                            flatIndexOnFloor,
                            value
                          )
                        )
                      }
                      onRemove={
                        editable
                          ? () =>
                              onBlockChanged?.(
                                removeFlatFromBlock(block, row.floor, flatIndexOnFloor)
                              )
                          : undefined
                      }
                    />
                  );
                })}
                {editable && (
                  <button
                    type="button"
                    className="community-flat-preview-add"
                    onClick={() => onBlockChanged?.(addFlatToBlock(block, row.floor))}
                  >
                    + Add Flat/Room
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlatPreviewTable;
